//----- Component --------------------------------------------------------/
// Developer : Daiana Curcio/Nicolas Pascucci
// Creation Date : 01/02/2024
// Version : 3
// Description : Resumen de las solicitudes de abastecimiento semanales
//------Changes ----------------------------------------------------------//
// Change Date : 04/11/2026
// Change by :
// Change description: Vista de tarjetas por planta y vista de tabla alternativa
//========================================================================/
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import "moment/locale/es";
import Navbar from "../Navbar";
import { UrlApi } from "../../services/apirest";
import { logEventos } from "../../services/services";
import { COLORES_PRODUCTOS } from "./coloresProductos";

const PRODUCTOS = [
  { clave: "butano", corto: "B", nombre: "Butano", color: "#1976D2" },
  { clave: "propano", corto: "P", nombre: "Propano", color: "#EF6C00" },
];

const COLOR = {
  fondo: "#F3E5F5",
  borde: "#7B1FA2",
  encabezado: "#E1BEE7",
};

const ESTADOS = {
  pendiente: { texto: "Pendiente", color: "default" },
  curso: { texto: "En curso", color: "primary" },
  completa: { texto: "Completa", color: "success" },
  revisar: { texto: "Revisar", color: "error" },
};

const COLUMNAS_CSV = [
  "Fecha            ",
  "Cantidad de Butano",
  "Cantidad de Propano",
  "Sucursal",
  "Usuario",
];

const ANCHOS_COLUMNAS = {
  sucursal: "24%",
  producto: "6%",
  dia: "9.5%",
  total: "13%",
};

const celdaSx = {
  py: 0.25,
  px: 0.75,
  fontSize: 12,
  textAlign: "center",
  whiteSpace: "nowrap",
};

const encabezadoSx = {
  ...celdaSx,
  fontSize: 11,
  fontWeight: 700,
  bgcolor: COLOR.encabezado,
};

const crearSemana = (inicio) =>
  [0, 1, 2, 3, 4, 5].map((dias) => {
    const fecha = inicio.clone().add(dias, "days");

    return {
      fecha: fecha.format("YYYY-MM-DD"),
      nombre: fecha.format("ddd"),
      dia: fecha.format("DD/MM"),
    };
  });

const crearSaldo = () => ({ disponible: 0, pedido: 0, tieneDatos: false });

const sumarSaldo = (saldo, disponible, pedido) => {
  const tieneDatos =
    (disponible !== null && disponible !== undefined) ||
    (pedido !== null && pedido !== undefined);

  if (!tieneDatos) return saldo;

  return {
    disponible: saldo.disponible + (Number(disponible) || 0),
    pedido: saldo.pedido + (Number(pedido) || 0),
    tieneDatos: true,
  };
};

const tieneValor = (saldo) =>
  saldo.tieneDatos && (saldo.disponible !== 0 || saldo.pedido !== 0);

const calcularPorcentaje = (saldo) => {
  if (!saldo.pedido) return 0;

  return Math.min(Math.max((saldo.disponible / saldo.pedido) * 100, 0), 100);
};

const obtenerEstado = (productos) => {
  if (productos.some((producto) => producto.total.disponible < 0)) {
    return "revisar";
  }

  const pedido = productos.reduce(
    (total, producto) => total + producto.total.pedido,
    0
  );
  const disponible = productos.reduce(
    (total, producto) => total + producto.total.disponible,
    0
  );

  if (disponible === 0) return "completa";
  if (disponible === pedido) return "pendiente";
  return "curso";
};

function BarraProducto({ producto, saldo }) {
  const porcentaje = calcularPorcentaje(saldo);
  const excedido = saldo.disponible < 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1,
          mb: 0.25,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          {producto.nombre}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: excedido ? "error.main" : "text.primary",
          }}
        >
          {excedido ? (
            <>
              Excedido en <b>{Math.abs(saldo.disponible)}</b> (pedido {saldo.pedido})
            </>
          ) : (
            <>
              Faltan <b>{saldo.disponible}</b> de {saldo.pedido}
            </>
          )}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={porcentaje}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: "grey.300",
          "& .MuiLinearProgress-bar": {
            bgcolor: producto.color,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
}

function ResumenSemana({ totales, filas }) {
  const completas = filas.filter(
    (fila) => obtenerEstado(fila.productos) === "completa"
  ).length;
  const revisar = filas.filter(
    (fila) => obtenerEstado(fila.productos) === "revisar"
  ).length;

  return (
    <Grid container spacing={1}>
      {PRODUCTOS.map((producto) => {
        const total = totales.find((item) => item.clave === producto.clave);
        const saldo = total?.total || crearSaldo();
        const excedido = saldo.disponible < 0;

        return (
          <Grid item xs={12} md={4} key={producto.clave}>
            <Box
              sx={{
                height: "100%",
                bgcolor: "white",
                border: "1px solid",
                borderColor: "divider",
                borderTop: `3px solid ${producto.color}`,
                borderRadius: 1,
                p: 1.25,
              }}
            >
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {producto.nombre}: {excedido ? "excedido en" : "faltan asignar"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: excedido ? "error.main" : "text.primary",
                }}
              >
                {Math.abs(saldo.disponible)}
                <Box
                  component="span"
                  sx={{ fontSize: 14, fontWeight: 400, color: "text.secondary" }}
                >
                  {" "}
                  {excedido ? `(pedido ${saldo.pedido})` : `de ${saldo.pedido}`}
                </Box>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={calcularPorcentaje(saldo)}
                sx={{
                  mt: 0.75,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.300",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: producto.color,
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </Grid>
        );
      })}

      <Grid item xs={12} md={4}>
        <Box
          sx={{
            height: "100%",
            bgcolor: "white",
            border: "1px solid",
            borderColor: "divider",
            borderTop: `3px solid ${COLOR.borde}`,
            borderRadius: 1,
            p: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Plantas con pedidos
          </Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>
            {filas.length}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
            {completas} {completas === 1 ? "completa" : "completas"}
            {revisar > 0 ? ` · ${revisar} para revisar` : ""}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

function TarjetaPlanta({ fila, semana }) {
  const [abierta, setAbierta] = useState(false);
  const estado = ESTADOS[obtenerEstado(fila.productos)];

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: "white",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.25,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {fila.nombre}
        </Typography>
        <Chip
          size="small"
          label={estado.texto}
          color={estado.color}
          sx={{ height: 20, fontSize: 11 }}
        />
      </Box>

      {fila.productos.map((producto) => (
        <BarraProducto
          key={producto.clave}
          producto={producto}
          saldo={producto.total}
        />
      ))}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          textAlign: "center",
          bgcolor: "grey.100",
          borderRadius: 1,
          py: 0.5,
        }}
      >
        {semana.map((dia) => {
          const pedido = fila.productos.reduce(
            (total, producto) => total + producto.dias[dia.fecha].pedido,
            0
          );

          return (
            <Box key={dia.fecha}>
              <Typography
                sx={{
                  fontSize: 10,
                  color: "text.secondary",
                  textTransform: "capitalize",
                }}
              >
                {dia.nombre}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {pedido || "-"}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Button
        size="small"
        sx={{ alignSelf: "flex-end", fontSize: 11 }}
        endIcon={abierta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setAbierta((valor) => !valor)}
      >
        {abierta ? "Ocultar detalle" : "Ver detalle"}
      </Button>

      <Collapse in={abierta} unmountOnExit>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...encabezadoSx, textAlign: "left", width: "28%" }}>
                Día
              </TableCell>
              {fila.productos.map((producto) => (
                <TableCell key={producto.clave} sx={encabezadoSx}>
                  {producto.corto}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {semana.map((dia) => (
              <TableRow key={dia.fecha}>
                <TableCell
                  sx={{
                    ...celdaSx,
                    textAlign: "left",
                    textTransform: "capitalize",
                  }}
                >
                  {dia.nombre} {dia.dia}
                </TableCell>
                {fila.productos.map((producto) => {
                  const saldo = producto.dias[dia.fecha];

                  return (
                    <TableCell
                      key={producto.clave}
                      sx={{
                        ...celdaSx,
                        bgcolor: COLORES_PRODUCTOS[producto.clave].fila,
                        color: saldo.disponible < 0 ? "error.main" : "inherit",
                      }}
                    >
                      {tieneValor(saldo)
                        ? `${saldo.disponible} / ${saldo.pedido}`
                        : "-"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
}

function CeldaSaldo({ saldo, sx }) {
  if (!tieneValor(saldo)) {
    return (
      <TableCell sx={{ ...celdaSx, color: "text.disabled", ...sx }}>-</TableCell>
    );
  }

  return (
    <TableCell
      sx={{
        ...celdaSx,
        color: saldo.disponible < 0 ? "error.main" : "inherit",
        ...sx,
      }}
    >
      {saldo.disponible} / {saldo.pedido}
    </TableCell>
  );
}

function TablaSolicitudes({ filas, totales, semana }) {
  return (
    <TableContainer sx={{ bgcolor: "white" }}>
      <Table size="small" sx={{ minWidth: 760, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: ANCHOS_COLUMNAS.sucursal }} />
          <col style={{ width: ANCHOS_COLUMNAS.producto }} />
          {semana.map((dia) => (
            <col key={dia.fecha} style={{ width: ANCHOS_COLUMNAS.dia }} />
          ))}
          <col style={{ width: ANCHOS_COLUMNAS.total }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...encabezadoSx, textAlign: "left" }}>
              Sucursal
            </TableCell>
            <TableCell sx={encabezadoSx}>Prod.</TableCell>
            {semana.map((dia) => (
              <TableCell
                key={dia.fecha}
                sx={{ ...encabezadoSx, textTransform: "capitalize" }}
              >
                {dia.nombre} {dia.dia}
              </TableCell>
            ))}
            <TableCell
              sx={{
                ...encabezadoSx,
                borderLeft: "2px solid",
                borderLeftColor: "divider",
              }}
            >
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((fila) =>
            fila.productos.map((producto, index) => {
              const colorFila = COLORES_PRODUCTOS[producto.clave].fila;

              return (
                <TableRow key={`${fila.id}-${producto.clave}`}>
                  {index === 0 ? (
                    <TableCell
                      rowSpan={fila.productos.length}
                      sx={{
                        ...celdaSx,
                        textAlign: "left",
                        fontWeight: 500,
                        verticalAlign: "top",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        bgcolor: "white",
                      }}
                    >
                      {fila.nombre}
                    </TableCell>
                  ) : null}
                  <TableCell
                    sx={{ ...celdaSx, fontWeight: 700, bgcolor: colorFila }}
                  >
                    {producto.corto}
                  </TableCell>
                  {semana.map((dia) => (
                    <CeldaSaldo
                      key={dia.fecha}
                      saldo={producto.dias[dia.fecha]}
                      sx={{ bgcolor: colorFila }}
                    />
                  ))}
                  <CeldaSaldo
                    saldo={producto.total}
                    sx={{
                      fontWeight: 700,
                      bgcolor: colorFila,
                      borderLeft: "2px solid",
                      borderLeftColor: "divider",
                    }}
                  />
                </TableRow>
              );
            })
          )}

          {totales.map((producto, index) => {
            const colorTotal = COLORES_PRODUCTOS[producto.clave].total;

            return (
              <TableRow key={`total-${producto.clave}`}>
                {index === 0 ? (
                  <TableCell
                    rowSpan={totales.length}
                    sx={{
                      ...celdaSx,
                      textAlign: "left",
                      fontWeight: 700,
                      bgcolor: COLOR.fondo,
                      verticalAlign: "top",
                    }}
                  >
                    TOTAL
                  </TableCell>
                ) : null}
                <TableCell
                  sx={{ ...celdaSx, fontWeight: 700, bgcolor: colorTotal }}
                >
                  {producto.corto}
                </TableCell>
                {semana.map((dia) => (
                  <CeldaSaldo
                    key={dia.fecha}
                    saldo={producto.dias[dia.fecha]}
                    sx={{ fontWeight: 700, bgcolor: colorTotal }}
                  />
                ))}
                <CeldaSaldo
                  saldo={producto.total}
                  sx={{
                    fontWeight: 700,
                    bgcolor: colorTotal,
                    borderLeft: "2px solid",
                    borderLeftColor: "divider",
                  }}
                />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const MatrizDobleEntrada = () => {
  const [inicioSemana, setInicioSemana] = useState(() => moment().day(8));
  const [vista, setVista] = useState("tarjetas");
  const [sucursales, setSucursales] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [datosCsv, setDatosCsv] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const inicioTexto = inicioSemana.format("YYYY-MM-DD");
  const semana = useMemo(() => crearSemana(inicioSemana), [inicioTexto]);
  const periodoTexto =
    inicioSemana.format("DD/MM") +
    " al " +
    inicioSemana.clone().add(5, "days").format("DD/MM/YYYY");

  useEffect(() => {
    logEventos(
      "Acceso al menú resumen semanal",
      "Resumen Semanal",
      "Ver resumen de pedidos semanales",
      localStorage.getItem("user")
    );
  }, []);

  useEffect(() => {
    let vigente = true;

    const desde = semana[0].fecha;
    const hasta = semana[semana.length - 1].fecha;

    const pedirLista = async (ruta) => {
      const res = await fetch(UrlApi + ruta);
      const data = await res.json().catch(() => null);

      if (!res.ok || !Array.isArray(data)) {
        throw new Error("Respuesta inválida en " + ruta);
      }

      return data;
    };

    setCargando(true);
    setError("");

    Promise.all([
      pedirLista("/sucursales/tipoPlanta"),
      pedirLista(`/solicitudes_abastecimiento/acotado/${desde}/${hasta}`),
      pedirLista(`/solicitudes_abastecimiento/csv/${desde}/${hasta}`),
    ])
      .then(([listaSucursales, listaSolicitudes, listaCsv]) => {
        if (!vigente) return;

        setSucursales(listaSucursales);
        setSolicitudes(listaSolicitudes);
        setDatosCsv(listaCsv);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) {
          setSucursales([]);
          setSolicitudes([]);
          setDatosCsv([]);
          setError(
            "No se pudo cargar el resumen. Recargá la pantalla o avisá a Sistemas."
          );
        }
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [semana]);

  const { filas, totales } = useMemo(() => {
    const registros = new Map();

    solicitudes.forEach((solicitud) => {
      const clave = `${solicitud.id_sucursal}|${moment(
        solicitud.para_fecha
      ).format("YYYY-MM-DD")}`;
      const lista = registros.get(clave) || [];
      lista.push(solicitud);
      registros.set(clave, lista);
    });

    const filasCalculadas = sucursales
      .map((sucursal) => {
        const productos = PRODUCTOS.map((producto) => {
          const dias = {};
          let total = crearSaldo();

          semana.forEach((dia) => {
            let saldo = crearSaldo();

            (registros.get(`${sucursal.id}|${dia.fecha}`) || []).forEach(
              (solicitud) => {
                saldo = sumarSaldo(
                  saldo,
                  solicitud[`viaje_${producto.clave}`],
                  solicitud[producto.clave]
                );
              }
            );

            dias[dia.fecha] = saldo;

            if (tieneValor(saldo)) {
              total = sumarSaldo(total, saldo.disponible, saldo.pedido);
            }
          });

          return { ...producto, dias, total };
        }).filter((producto) => tieneValor(producto.total));

        return {
          id: sucursal.id,
          nombre: sucursal.nombre,
          productos,
        };
      })
      .filter((fila) => fila.productos.length > 0);

    const totalesCalculados = PRODUCTOS.map((producto) => {
      const dias = {};
      let total = crearSaldo();

      semana.forEach((dia) => {
        let saldo = crearSaldo();

        filasCalculadas.forEach((fila) => {
          const productoFila = fila.productos.find(
            (item) => item.clave === producto.clave
          );
          const saldoDia = productoFila?.dias[dia.fecha];

          if (saldoDia && tieneValor(saldoDia)) {
            saldo = sumarSaldo(saldo, saldoDia.disponible, saldoDia.pedido);
          }
        });

        dias[dia.fecha] = saldo;

        if (tieneValor(saldo)) {
          total = sumarSaldo(total, saldo.disponible, saldo.pedido);
        }
      });

      return { ...producto, dias, total };
    }).filter((producto) => tieneValor(producto.total));

    return { filas: filasCalculadas, totales: totalesCalculados };
  }, [solicitudes, sucursales, semana]);

  const exportarCsv = () => {
    const exportador = new ExportToCsv({
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: false,
      filename: "Lista Solicitudes de Abastecimiento",
      headers: COLUMNAS_CSV,
    });

    exportador.generateCsv(datosCsv);
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          bgcolor: "grey.50",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderTop: "3px solid #00796B",
            borderRadius: 1,
            bgcolor: "#E0F2F1",
            px: 1.5,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 0.5,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Resumen de solicitudes</Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Semana anterior">
              <IconButton
                size="small"
                onClick={() =>
                  setInicioSemana((fecha) => fecha.clone().subtract(7, "days"))
                }
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2">
              Semana: <b>{periodoTexto}</b>
            </Typography>
            <Tooltip title="Semana siguiente">
              <IconButton
                size="small"
                onClick={() =>
                  setInicioSemana((fecha) => fecha.clone().add(7, "days"))
                }
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={vista}
            sx={{ bgcolor: "white" }}
            onChange={(event, valor) => {
              if (valor) setVista(valor);
            }}
          >
            <ToggleButton value="tarjetas" sx={{ py: 0.25, fontSize: 11 }}>
              <ViewModuleIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Tarjetas
            </ToggleButton>
            <ToggleButton value="tabla" sx={{ py: 0.25, fontSize: 11 }}>
              <TableRowsIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Tabla
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            size="small"
            variant="outlined"
            sx={{ bgcolor: "white" }}
            endIcon={<FileDownloadIcon />}
            disabled={cargando || datosCsv.length === 0}
            onClick={exportarCsv}
          >
            Exportar CSV
          </Button>
        </Box>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {cargando ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!cargando && !error && filas.length === 0 ? (
          <Alert severity="info">No hay solicitudes en esta semana.</Alert>
        ) : null}

        {!cargando && !error && filas.length > 0 ? (
          <>
            <ResumenSemana totales={totales} filas={filas} />

            {vista === "tarjetas" ? (
              <Grid container spacing={1} alignItems="stretch">
                {filas.map((fila) => (
                  <Grid item xs={12} sm={6} lg={4} xl={3} key={fila.id}>
                    <TarjetaPlanta fila={fila} semana={semana} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderTop: `3px solid ${COLOR.borde}`,
                  borderRadius: 1,
                  bgcolor: COLOR.fondo,
                  p: 1,
                }}
              >
                <Typography sx={{ mb: 0.75, fontSize: 13, fontWeight: 700 }}>
                  Solicitudes
                  <Box
                    component="span"
                    sx={{ ml: 0.5, fontWeight: 400, color: "text.secondary" }}
                  >
                    (disponibles / pedidos)
                  </Box>
                </Typography>
                <TablaSolicitudes
                  filas={filas}
                  totales={totales}
                  semana={semana}
                />
              </Box>
            )}
          </>
        ) : null}
      </Box>
    </>
  );
};

export default MatrizDobleEntrada;